import { useState } from "react";
import { Loader2, Pencil, Trash2, X } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Select } from "@/components/ui/select";
import { useAuth } from "@/hooks/use-auth";
import {
	useAddUserToTeam,
	useAdminTeams,
	useAdminUsers,
	useRemoveUserFromTeam,
	useUpdateAdminUser,
	useUpdateUserTeam,
} from "@/hooks/use-admin-users";
import type { AdminUser, AssignableTeamRole, SiteRole } from "@/lib/access";
import { useTranslations } from "@/hooks/use-translations";
import {
	AccessPicker,
	SITE_ROLES,
	TEAM_ROLES,
	useManagerLabels,
	type AccessValue,
} from "./access-picker";

const ROLE_TONE = { super_admin: "brand", admin: "info", user: "neutral" } as const;

/** Personal space access, as badges. */
function AccessSummary({ user }: { user: AdminUser }) {
	const t = useTranslations("manager.users");
	const labels = useManagerLabels();
	if (user.siteRole === "super_admin" || user.fullAccess) return <Badge tone="success">{t("fullAccess")}</Badge>;
	if (user.featureAccess.length === 0) return <Badge tone="warning">{t("noFeatures")}</Badge>;
	return (
		<>
			{user.featureAccess.map((feature) => (
				<Badge key={feature}>{labels.featureShort(feature)}</Badge>
			))}
		</>
	);
}

export function UsersList() {
	const users = useAdminUsers();
	const { user: me } = useAuth();
	const [openId, setOpenId] = useState<string | null>(null);
	const t = useTranslations("manager.users");
	const labels = useManagerLabels();

	/** "Team · Role · what they can use there" */
	const teamSummary = (place: AdminUser["teams"][number]) => {
		const access =
			place.role === "owner" || place.fullAccess
				? t("fullAccess")
				: place.featureAccess.length
					? place.featureAccess.map(labels.featureShort).join(", ")
					: t("teamNothing");
		return `${place.teamName} · ${labels.teamRoleShort(place.role)} · ${access}`;
	};

	// The page's card (Manager page), styled like the Connectors card: a title
	// section, then one row per user, divided by thin lines.
	return (
		<>
			<div className="p-5">
				<h3 className="text-sm font-medium text-text-primary">{t("title")}</h3>
				<p className="text-sm text-text-tertiary">{t("description")}</p>
				{users.isLoading && (
					<div className="flex justify-center py-8">
						<Loader2 size={20} className="animate-spin text-text-tertiary" />
					</div>
				)}
				{users.isError && <p className="mt-3 text-sm text-danger">{t("loadError")}</p>}
			</div>

			<ul className="divide-y divide-border-subtle border-t border-border-subtle">
				{(users.data ?? []).map((user) => {
					const open = openId === user.id;
					return (
						<li key={user.id} className="px-5 py-3">
							<div className="flex flex-wrap items-center gap-x-3 gap-y-2">
								<div className="min-w-0 flex-1">
									<p className="truncate text-sm font-medium text-text-primary">
										{user.name}
										{user.id === me?.id && <span className="text-text-tertiary"> {t("you")}</span>}
									</p>
									<p className="truncate text-xs text-text-tertiary">{user.email}</p>
								</div>
								<div className="flex flex-wrap items-center gap-1.5">
									<Badge tone={ROLE_TONE[user.siteRole]}>{labels.siteRole(user.siteRole)}</Badge>
									<AccessSummary user={user} />
									{!user.isActive && <Badge tone="danger">{t("deactivated")}</Badge>}
								</div>
								<Button
									variant="secondary"
									size="sm"
									onClick={() => setOpenId(open ? null : user.id)}
									aria-expanded={open}
								>
									{open ? <X size={14} /> : <Pencil size={14} />}
									{t(open ? "close" : "edit")}
								</Button>
							</div>
							{user.teams.length > 0 && !open && (
								<p className="mt-1 text-xs text-text-secondary">{user.teams.map(teamSummary).join("; ")}</p>
							)}
							{open && <UserEditor key={user.id} user={user} isSelf={user.id === me?.id} />}
						</li>
					);
				})}
			</ul>
		</>
	);
}

function UserEditor({ user, isSelf }: { user: AdminUser; isSelf: boolean }) {
	const [siteRole, setSiteRole] = useState<SiteRole>(user.siteRole);
	const [access, setAccess] = useState<AccessValue>({
		fullAccess: user.fullAccess,
		featureAccess: user.featureAccess,
	});
	const update = useUpdateAdminUser();
	const t = useTranslations("manager.users");
	const labels = useManagerLabels();

	const changed =
		siteRole !== user.siteRole ||
		access.fullAccess !== user.fullAccess ||
		[...access.featureAccess].sort().join() !== [...user.featureAccess].sort().join();

	return (
		<div className="mt-4 space-y-5 rounded-xl border border-border-subtle bg-bg-canvas p-4">
			<div className="grid gap-5 md:grid-cols-2">
				<div className="space-y-2">
					<Select
						id={`role-${user.id}`}
						label={t("role")}
						value={siteRole}
						onChange={(e) => setSiteRole(e.target.value as SiteRole)}
						disabled={isSelf || update.isPending}
					>
						{SITE_ROLES.map((role) => (
							<option key={role} value={role}>
								{labels.siteRole(role)}
							</option>
						))}
					</Select>
					{isSelf && (
						<p className="text-xs text-text-tertiary">{t("selfNote")}</p>
					)}
				</div>
				<AccessPicker
					idPrefix={`edit-${user.id}`}
					value={access}
					onChange={setAccess}
					disabled={update.isPending}
				/>
			</div>

			<div className="flex flex-wrap items-center gap-2">
				<Button
					variant="secondary"
					disabled={!changed || update.isPending}
					onClick={() => update.mutate({ userId: user.id, changes: { siteRole, ...access } })}
				>
					{update.isPending && <Loader2 size={14} className="animate-spin" />}
					{t("save")}
				</Button>
				{user.isActive ? (
					<Button
						variant="danger"
						disabled={isSelf || update.isPending}
						onClick={() => update.mutate({ userId: user.id, changes: { isActive: false } })}
					>
						{t("deactivate")}
					</Button>
				) : (
					<Button
						variant="secondary"
						disabled={update.isPending}
						onClick={() => update.mutate({ userId: user.id, changes: { isActive: true } })}
					>
						{t("activate")}
					</Button>
				)}
			</div>

			<UserTeams user={user} />
		</div>
	);
}

const NO_TEAM_ACCESS: AccessValue = { fullAccess: false, featureAccess: [] };
const sameAccess = (a: AccessValue, b: AccessValue) =>
	a.fullAccess === b.fullAccess && [...a.featureAccess].sort().join() === [...b.featureAccess].sort().join();

function RoleSelect({
	id,
	label,
	value,
	onChange,
	disabled,
}: {
	id: string;
	label: string;
	value: AssignableTeamRole;
	onChange: (role: AssignableTeamRole) => void;
	disabled?: boolean;
}) {
	const labels = useManagerLabels();
	return (
		<Select
			id={id}
			aria-label={label}
			value={value}
			disabled={disabled}
			onChange={(e) => onChange(e.target.value as AssignableTeamRole)}
			className="w-48"
		>
			{TEAM_ROLES.map((r) => (
				<option key={r} value={r}>
					{labels.teamRole(r)}
				</option>
			))}
		</Select>
	);
}

/** One team the user is in: their role there and what they can use there. */
function TeamPlace({ user, place }: { user: AdminUser; place: AdminUser["teams"][number] }) {
	const update = useUpdateUserTeam();
	const remove = useRemoveUserFromTeam();
	const saved = { fullAccess: place.fullAccess, featureAccess: place.featureAccess };
	const [access, setAccess] = useState<AccessValue>(saved);
	const busy = update.isPending || remove.isPending;
	const t = useTranslations("manager.users");

	if (place.role === "owner") {
		return (
			<li className="flex flex-wrap items-center gap-2 rounded-xl border border-border-subtle bg-bg-surface p-3">
				<span className="min-w-0 flex-1 truncate text-sm font-medium text-text-primary">{place.teamName}</span>
				<Badge tone="brand" title={t("ownerLocked")}>
					{t("ownerBadge")}
				</Badge>
			</li>
		);
	}

	return (
		<li className="space-y-3 rounded-xl border border-border-subtle bg-bg-surface p-3">
			<div className="flex flex-wrap items-center gap-2">
				<span className="min-w-0 flex-1 truncate text-sm font-medium text-text-primary">{place.teamName}</span>
				<RoleSelect
					id={`team-role-${user.id}-${place.teamId}`}
					label={t("roleIn", { team: place.teamName })}
					value={place.role}
					disabled={busy}
					onChange={(role) => update.mutate({ userId: user.id, teamId: place.teamId, changes: { role } })}
				/>
				<Button
					variant="ghost"
					size="icon-sm"
					disabled={busy}
					aria-label={t("removeFrom", { team: place.teamName })}
					title={t("removeTitle")}
					onClick={() => remove.mutate({ userId: user.id, teamId: place.teamId })}
					className="hover:text-danger"
				>
					<Trash2 size={16} />
				</Button>
			</div>
			<AccessPicker
				idPrefix={`team-${user.id}-${place.teamId}`}
				team
				value={access}
				onChange={setAccess}
				disabled={busy}
			/>
			<Button
				variant="secondary"
				size="sm"
				disabled={busy || sameAccess(access, saved)}
				onClick={() => update.mutate({ userId: user.id, teamId: place.teamId, changes: access })}
			>
				{t("saveTeamAccess")}
			</Button>
		</li>
	);
}

function UserTeams({ user }: { user: AdminUser }) {
	const teams = useAdminTeams();
	const add = useAddUserToTeam();
	const [teamId, setTeamId] = useState("");
	const [role, setRole] = useState<AssignableTeamRole>("member");
	const [access, setAccess] = useState<AccessValue>(NO_TEAM_ACCESS);
	const available = (teams.data ?? []).filter((team) => !user.teams.some((place) => place.teamId === team.id));
	const t = useTranslations("manager.users");

	return (
		<div className="space-y-3 border-t border-border-subtle pt-4">
			<p className="text-sm font-medium text-text-primary">{t("teams")}</p>
			{user.teams.length === 0 && <p className="text-sm text-text-tertiary">{t("noTeams")}</p>}
			<ul className="space-y-2">
				{user.teams.map((place) => (
					<TeamPlace key={place.teamId} user={user} place={place} />
				))}
			</ul>

			{available.length > 0 && (
				<div className="space-y-3 rounded-xl border border-border-subtle bg-bg-surface p-3">
					<div className="flex flex-wrap items-center gap-2">
						<Select
							aria-label={t("teamToAdd")}
							value={teamId}
							onChange={(e) => setTeamId(e.target.value)}
							disabled={add.isPending}
							className="w-56"
						>
							<option value="">{t("addToTeamPlaceholder")}</option>
							{available.map((team) => (
								<option key={team.id} value={team.id}>
									{team.name}
								</option>
							))}
						</Select>
						<RoleSelect
							id={`new-team-role-${user.id}`}
							label={t("newTeamRole")}
							value={role}
							onChange={setRole}
							disabled={add.isPending}
						/>
					</div>
					{teamId && (
						<AccessPicker
							idPrefix={`new-team-${user.id}`}
							team
							value={access}
							onChange={setAccess}
							disabled={add.isPending}
						/>
					)}
					<Button
						variant="secondary"
						size="sm"
						disabled={!teamId || add.isPending}
						onClick={() =>
							add.mutate(
								{ userId: user.id, teamId, role, ...access },
								{
									onSuccess: () => {
										setTeamId("");
										setAccess(NO_TEAM_ACCESS);
									},
								},
							)
						}
					>
						{t("addToTeam")}
					</Button>
				</div>
			)}
		</div>
	);
}
